import { create } from 'zustand';
import { createWebSocketConnection, parseStreamMessage, sendChatOverWs, cancelChatOverWs, WebSocketAuthError, type StreamChunk } from '../api/streaming';
import { useChatStore } from './chatStore';

export interface ToolCallRecord {
  id: string;           // tool_call_id from backend
  agent: string;        // "PPL Agent"
  tool: string;         // "search_documents"
  args: Record<string, unknown>;
  content: string;      // accumulated chunks
  status: 'running' | 'done' | 'failed';
  error?: string;
  startedAt: number;
  completedAt?: number;
}

/** @deprecated Use ToolCallRecord — kept for backward compat with cached messages */
export interface AgentToolCall {
  agent: string;
  content: string;
  status: 'running' | 'done' | 'failed';
  error?: string;
  startedAt: number;
  completedAt?: number;
}

export interface MessageStreamRecord {
  messageId: string;
  statusMessages: string[];
  toolCalls: ToolCallRecord[];
  answer: string;
  metadata: Record<string, unknown>;
  complete: boolean;
}

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

interface ReadyConnection {
  socket: WebSocket;
  group: string;
}

interface EnsureConnectionOptions {
  forceNew?: boolean;
  timeoutMs?: number;
}

interface StreamingStore {
    activeStreamId: string | null;
    streamContent: string;
    isStreaming: boolean;
    webSocket: WebSocket | null;
    webSocketGroup: string | null;
    currentStreamRecord: MessageStreamRecord | null;
    lastDoneResponse: Record<string, unknown> | null;
    lastStreamError: Record<string, unknown> | null;
    cancelledStreamId: string | null;
    connectionStatus: ConnectionStatus;
    reconnectAttempts: number;

    // Legacy compat — derived from currentStreamRecord
    streamChunks: Record<string, string>;

    startStream: (streamId: string) => void;
    endStream: () => void;
    connect: () => Promise<void>;
    ensureConnected: (options?: EnsureConnectionOptions) => Promise<ReadyConnection>;
    resetConnection: () => Promise<void>;
    disconnect: () => void;
    stopThinking: () => void;
    sendChat: (payload: Record<string, unknown>) => void;
    cancelChat: () => void;
}

const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_BASE_MS = 1000;
const RECONNECT_CAP_MS = 16000;

let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let connectPromise: Promise<void> | null = null;

function closeSocket(socket: WebSocket | null, code = 1000, reason = 'reset'): void {
  if (!socket) return;
  try { (socket as any)._intentionalClose = true; } catch {}
  const pingInterval = (socket as any)._pingInterval;
  if (pingInterval) clearInterval(pingInterval);
  try {
    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      socket.close(code, reason);
    }
  } catch {}
}

function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function isSocketOpen(socket: WebSocket | null): socket is WebSocket {
  return socket?.readyState === WebSocket.OPEN;
}

function buildStreamChunksFromRecord(record: MessageStreamRecord | null): Record<string, string> {
  if (!record) return {};
  const chunks: Record<string, string> = {};
  const totalByAgent = new Map<string, number>();
  const seenByAgent = new Map<string, number>();

  for (const tc of record.toolCalls) {
    const agent = tc.agent.trim();
    if (agent) totalByAgent.set(agent, (totalByAgent.get(agent) || 0) + 1);
  }

  for (const tc of record.toolCalls) {
    const agent = tc.agent.trim();
    if (!agent) continue;
    const seen = (seenByAgent.get(agent) || 0) + 1;
    seenByAgent.set(agent, seen);
    const key = (totalByAgent.get(agent) || 0) > 1 ? `${agent} #${seen}` : agent;
    chunks[key] = tc.content;
  }
  return chunks;
}

function getOrCreateRecord(state: StreamingStore): MessageStreamRecord {
  return state.currentStreamRecord || {
    messageId: state.activeStreamId || '',
    statusMessages: [],
    toolCalls: [],
    answer: '',
    metadata: {},
    complete: false,
  };
}

function shouldApplyStreamUpdate(state: StreamingStore, msg: Pick<StreamChunk, 'id'>): boolean {
  const record = state.currentStreamRecord;
  if (state.cancelledStreamId && (state.cancelledStreamId === msg.id || state.cancelledStreamId === record?.messageId)) {
    return false;
  }
  if (record?.complete) {
    return false;
  }
  if (msg.id && record?.messageId && msg.id !== record.messageId) {
    return false;
  }
  if (msg.id && state.activeStreamId && msg.id !== state.activeStreamId) {
    return false;
  }
  return true;
}

function buildDerivedState(record: MessageStreamRecord) {
  const streamChunks = buildStreamChunksFromRecord(record);
  // `streamContent` was a legacy concatenated markdown blob. Nothing in the
  // current UI consumes it, and rebuilding one huge string on every chunk makes
  // the browser appear to keep streaming after the backend has already stopped.
  // Keep the field for compatibility, but don't duplicate the live tool output.
  return { streamChunks, streamContent: '' };
}

export const useStreamingStore = create<StreamingStore>((set, get) => ({
  activeStreamId: null,
  streamContent: '',
  isStreaming: false,
  webSocket: null,
  webSocketGroup: null,
  currentStreamRecord: null,
  lastDoneResponse: null,
  lastStreamError: null,
  cancelledStreamId: null,
  connectionStatus: 'disconnected',
  reconnectAttempts: 0,
  streamChunks: {},

  startStream: (streamId: string) => {
    set({
      activeStreamId: streamId,
      streamContent: '',
      isStreaming: true,
      currentStreamRecord: {
        messageId: streamId,
        statusMessages: [],
        toolCalls: [],
        answer: '',
        metadata: {},
        complete: false,
      },
      lastDoneResponse: null,
      lastStreamError: null,
      cancelledStreamId: null,
      streamChunks: {},
    });
  },

  endStream: () => {
    set({
      activeStreamId: null,
      streamContent: '',
      isStreaming: false,
      streamChunks: {},
    });
  },

  connect: async () => {
    const existing = get().webSocket;
    if (existing && existing.readyState === WebSocket.OPEN) {
      return;
    }

    if (connectPromise) {
      return connectPromise;
    }

    connectPromise = (async () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }

      const status = get().connectionStatus;
      set({ connectionStatus: status === 'reconnecting' ? 'reconnecting' : 'connecting' });

      try {
        const connection = await createWebSocketConnection();
        if (!connection) {
          throw new Error('createWebSocketConnection returned null');
        }

        const { socket, group } = connection;

        socket.onmessage = (event: MessageEvent) => {
          if (get().webSocket !== socket) {
            return;
          }

          try {
          const msg = parseStreamMessage(event.data) as StreamChunk & Record<string, any>;
          const type = msg.type;

          // ── Upload processing notifications (dispatch to chatStore) ──
          if (type === 'upload_complete' || type === 'upload_partial' || type === 'upload_failed') {
            const conversationId = msg.conversation_id;
            if (conversationId) {
              useChatStore.getState().setFileProcessingStatus(
                conversationId,
                type === 'upload_complete' ? 'complete' : type === 'upload_partial' ? 'partial' : 'failed',
                type === 'upload_failed' || type === 'upload_partial'
                  ? (msg.error || 'Processing failed')
                  : undefined,
              );
            }
            return;
          }

          // ── New structured events ──

          if (type === 'status') {
            set((state) => {
              if (!shouldApplyStreamUpdate(state, msg)) return {};
              const record = getOrCreateRecord(state);
              const updated = {
                ...record,
                statusMessages: [...record.statusMessages, msg.content || ''],
              };
              return { isStreaming: true, currentStreamRecord: updated, ...buildDerivedState(updated) };
            });
            return;
          }

          if (type === 'tool_call_start') {
            set((state) => {
              if (!shouldApplyStreamUpdate(state, msg)) return {};
              const record = getOrCreateRecord(state);
              const tcId = msg.tool_call_id || '';
              const existingIdx = tcId ? record.toolCalls.findIndex(tc => tc.id === tcId) : -1;
              const newCall: ToolCallRecord = {
                id: tcId,
                agent: msg.agent || '',
                tool: msg.tool || '',
                args: msg.args || {},
                content: '',
                status: 'running',
                startedAt: Date.now(),
              };
              const updatedCalls = [...record.toolCalls];
              if (existingIdx >= 0) {
                updatedCalls[existingIdx] = {
                  ...updatedCalls[existingIdx],
                  agent: newCall.agent || updatedCalls[existingIdx].agent,
                  tool: newCall.tool || updatedCalls[existingIdx].tool,
                  args: Object.keys(newCall.args).length ? newCall.args : updatedCalls[existingIdx].args,
                  status: 'running',
                };
              } else {
                updatedCalls.push(newCall);
              }
              const updated = { ...record, toolCalls: updatedCalls };
              return { isStreaming: true, currentStreamRecord: updated, ...buildDerivedState(updated) };
            });
            return;
          }

          if (type === 'tool_call_chunk') {
            set((state) => {
              if (!shouldApplyStreamUpdate(state, msg)) return {};
              const record = getOrCreateRecord(state);
              const tcId = msg.tool_call_id || '';
              const chunk = msg.chunk || '';
              const existingCall = record.toolCalls.find(tc => tc.id === tcId);
              if (existingCall?.status === 'done' || existingCall?.status === 'failed') return {};
              const updatedCalls = existingCall
                ? record.toolCalls.map(tc =>
                    tc.id === tcId
                      ? {
                          ...tc,
                          content: tc.content + chunk,
                          agent: msg.agent || tc.agent,
                        }
                      : tc
                  )
                : [
                    ...record.toolCalls,
                    {
                      id: tcId || `tool_${Date.now()}`,
                      agent: msg.agent || msg.tool || 'Tool',
                      tool: msg.tool || '',
                      args: msg.args || {},
                      content: chunk,
                      status: 'running' as const,
                      startedAt: Date.now(),
                    },
                  ];
              // Do NOT mirror tool_call_chunk into the answer: the chunk
              // payload is the raw tool output (RAG chunks, Genie tables)
              // which belongs in the Show-Thinking accordion, not the
              // visible answer. The final answer arrives via answer_chunk
              // events and/or the `done` payload.
              const updated = { ...record, toolCalls: updatedCalls };
              return { isStreaming: true, currentStreamRecord: updated, ...buildDerivedState(updated) };
            });
            return;
          }

          if (type === 'tool_call_end') {
            set((state) => {
              if (!shouldApplyStreamUpdate(state, msg)) return {};
              const record = getOrCreateRecord(state);
              const tcId = msg.tool_call_id || '';
              const error = typeof msg.error === 'string' ? msg.error : '';
              const updatedCalls = record.toolCalls.map(tc =>
                tc.id === tcId
                  ? { ...tc, status: (error ? 'failed' : 'done') as ToolCallRecord['status'], error: error || tc.error, completedAt: Date.now() }
                  : tc
              );
              const updated = { ...record, toolCalls: updatedCalls };
              const terminalSingleTool = updatedCalls.length === 1
                && updatedCalls.every(tc => tc.status === 'done' || tc.status === 'failed')
                && Boolean(record.answer.trim());
              return {
                isStreaming: !terminalSingleTool,
                currentStreamRecord: updated,
                ...buildDerivedState(updated),
              };
            });
            return;
          }

          if (type === 'answer_chunk') {
            set((state) => {
              if (!shouldApplyStreamUpdate(state, msg)) return {};
              const record = getOrCreateRecord(state);
              const updated = { ...record, answer: record.answer + (msg.chunk || '') };
              return { isStreaming: true, currentStreamRecord: updated, ...buildDerivedState(updated) };
            });
            return;
          }

          // ── Legacy chunk event (backward compat) ──

          if (type === 'chunk') {
            set((state) => {
              if (!shouldApplyStreamUpdate(state, msg)) return {};
              const record = getOrCreateRecord(state);
              const agent = (msg.agent || '').trim();
              const hasStructured = record.toolCalls.some(tc => !tc.id.startsWith('legacy_')) || record.statusMessages.length > 0;

              // Route legacy chunks into matching structured tool call by agent name
              if (hasStructured && agent) {
                const idx = record.toolCalls.findIndex(tc => tc.agent === agent);
                if (idx >= 0) {
                  const updatedCalls = [...record.toolCalls];
                  updatedCalls[idx] = {
                    ...updatedCalls[idx],
                    content: updatedCalls[idx].content + (msg.chunk || ''),
                  };
                  const updated = { ...record, toolCalls: updatedCalls };
                  return { isStreaming: true, currentStreamRecord: updated, ...buildDerivedState(updated) };
                }
                return {};
              }
              const isOrchestratorAgent = /^orchestrator\s*agent$/i.test(agent);
              let updatedRecord: MessageStreamRecord;

              if (agent && !isOrchestratorAgent) {
                const existingIdx = record.toolCalls.findIndex(tc => tc.agent === agent);
                const updatedCalls = [...record.toolCalls];
                if (existingIdx >= 0) {
                  updatedCalls[existingIdx] = {
                    ...updatedCalls[existingIdx],
                    content: updatedCalls[existingIdx].content + (msg.chunk || ''),
                    status: 'running',
                  };
                } else {
                  updatedCalls.push({
                    id: `legacy_${agent}`,
                    agent,
                    tool: '',
                    args: {},
                    content: msg.chunk || '',
                    status: 'running',
                    startedAt: Date.now(),
                  });
                }
                updatedRecord = { ...record, toolCalls: updatedCalls };
              } else {
                updatedRecord = { ...record, answer: record.answer + (msg.chunk || '') };
              }

              return { isStreaming: true, currentStreamRecord: updatedRecord, ...buildDerivedState(updatedRecord) };
            });
            return;
          }

          // ── Done ──

          if (type === 'done') {
            set((state) => {
              const record = state.currentStreamRecord;
              if (state.cancelledStreamId && (state.cancelledStreamId === msg.id || state.cancelledStreamId === record?.messageId)) {
                return {};
              }
              if (msg.id && record?.messageId && msg.id !== record.messageId) {
                return {};
              }
              if (msg.id && state.activeStreamId && msg.id !== state.activeStreamId) {
                return {};
              }
              const assembled = record?.answer?.trim() || '';
              const doneAnswer = (msg.response as any)?.answer || '';
              const finalAnswer = doneAnswer || assembled;

              const streamCalls = (record?.toolCalls || []).map(tc =>
                tc.status === 'running' ? { ...tc, status: 'done' as const, completedAt: Date.now() } : tc
              );
              const backendToolCalls = (msg.response as any)?.toolCalls;
              // Only trust the backend's tool-call list if it actually contains
              // entries; otherwise keep the live stream calls so the accordion
              // doesn't get wiped on `done`.
              const finalCalls = Array.isArray(backendToolCalls) && backendToolCalls.length > 0
                ? backendToolCalls
                : streamCalls;

              const finalRecord: MessageStreamRecord = {
                messageId: record?.messageId || msg.id || '',
                statusMessages: record?.statusMessages || [],
                toolCalls: finalCalls,
                answer: finalAnswer,
                metadata: msg.response || {},
                complete: true,
              };

              return {
                activeStreamId: null,
                isStreaming: false,
                webSocket: socket,
                webSocketGroup: state.webSocketGroup,
                connectionStatus: 'connected',
                reconnectAttempts: 0,
                currentStreamRecord: finalRecord,
                lastDoneResponse: {
                  ...(msg.response || {}),
                  answer: finalAnswer,
                  toolCalls: finalCalls,
                  stream_id: msg.id || finalRecord.messageId,
                },
                lastStreamError: null,
                ...buildDerivedState(finalRecord),
              };
            });
            return;
          }

          if (type === 'error') {
            console.error('Stream error:', msg.detail);
            const detail = msg.detail || msg.error || 'Chat request failed';
            set((state) => {
              const record = getOrCreateRecord(state);
              const failedCalls = record.toolCalls.map(tc =>
                tc.status === 'running'
                  ? { ...tc, status: 'failed' as const, error: detail, completedAt: Date.now() }
                  : tc
              );
              const finalRecord = {
                ...record,
                toolCalls: failedCalls,
                metadata: { ...(record.metadata || {}), error_code: 'STREAM_ERROR', error: detail },
                complete: true,
              };
              return {
                activeStreamId: null,
                isStreaming: false,
                connectionStatus: 'disconnected',
                currentStreamRecord: finalRecord,
                lastStreamError: { id: msg.id || record.messageId, detail, error_code: 'STREAM_ERROR' },
                ...buildDerivedState(finalRecord),
              };
            });
            closeSocket(socket, 1000, 'error');
            return;
          }
          // pong / auth_ok — no action
          } catch {
            // parse error — ignore
          }
        };

        socket.onclose = () => {
          const intentional = (socket as any)._intentionalClose === true;
          const pingInterval = (socket as any)._pingInterval;
          if (pingInterval) clearInterval(pingInterval);

          const isCurrentSocket = get().webSocket === socket;
          if (!isCurrentSocket) {
            return;
          }

          set({ webSocket: null });

          const snapshot = get();
          const activeRecord = snapshot.currentStreamRecord;
          const wasStreaming = snapshot.isStreaming || !!snapshot.activeStreamId;
          if (!intentional && wasStreaming) {
            const messageId = activeRecord?.messageId || snapshot.activeStreamId || '';
            const interruption = 'The streaming connection was interrupted. Please retry.';
            const answer = activeRecord?.answer?.trim()
              ? `${activeRecord.answer.trim()}\n\n${interruption}`
              : interruption;
            const failedCalls = (activeRecord?.toolCalls || []).map(tc =>
              tc.status === 'running'
                ? { ...tc, status: 'failed' as const, error: interruption, completedAt: Date.now() }
                : tc
            );
            const finalRecord: MessageStreamRecord = {
              messageId,
              statusMessages: activeRecord?.statusMessages || [],
              toolCalls: failedCalls,
              answer,
              metadata: { ...(activeRecord?.metadata || {}), error_code: 'WS_DISCONNECTED' },
              complete: true,
            };
            set({
              activeStreamId: null,
              isStreaming: false,
              connectionStatus: 'disconnected',
              reconnectAttempts: 0,
              currentStreamRecord: finalRecord,
              lastDoneResponse: { message_id: messageId, answer, error_code: 'WS_DISCONNECTED', toolCalls: failedCalls },
              ...buildDerivedState(finalRecord),
            });
            return;
          }

          if (intentional) {
            set({ connectionStatus: 'disconnected', isStreaming: false });
            return;
          }

          const attempts = get().reconnectAttempts;
          if (attempts >= MAX_RECONNECT_ATTEMPTS) {
            set({ connectionStatus: 'disconnected', isStreaming: false });
            console.error('WebSocket max reconnect attempts reached');
            return;
          }

          set({ connectionStatus: 'reconnecting', reconnectAttempts: attempts + 1 });
          const delay = Math.min(RECONNECT_BASE_MS * Math.pow(2, attempts), RECONNECT_CAP_MS);
          reconnectTimer = setTimeout(() => {
            get().connect();
          }, delay);
        };

        set({
          webSocket: socket,
          webSocketGroup: group,
          connectionStatus: 'connected',
          reconnectAttempts: 0,
        });
      } catch (error) {
        if (error instanceof WebSocketAuthError) {
          set({ connectionStatus: 'disconnected', isStreaming: false });
          console.error(error.message);
          throw error;
        }

        const attempts = get().reconnectAttempts;
        if (attempts < MAX_RECONNECT_ATTEMPTS) {
          set({ connectionStatus: 'reconnecting', reconnectAttempts: attempts + 1 });
          const delay = Math.min(RECONNECT_BASE_MS * Math.pow(2, attempts), RECONNECT_CAP_MS);
          reconnectTimer = setTimeout(() => {
            get().connect();
          }, delay);
        } else {
          set({ connectionStatus: 'disconnected' });
          console.error('WebSocket connection failed after max retries');
        }
      }
    })();

    try {
      await connectPromise;
    } finally {
      connectPromise = null;
    }
  },

  ensureConnected: async (options = {}) => {
    const { forceNew = false, timeoutMs = 45_000 } = options;

    if (!forceNew) {
      const { webSocket, webSocketGroup } = get();
      if (isSocketOpen(webSocket) && webSocketGroup) {
        return { socket: webSocket, group: webSocketGroup };
      }
    }

    if (forceNew) {
      await get().resetConnection();
    } else {
      await get().connect();
    }

    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      const { webSocket, webSocketGroup, connectionStatus } = get();
      if (isSocketOpen(webSocket) && webSocketGroup) {
        return { socket: webSocket, group: webSocketGroup };
      }

      if (connectionStatus === 'disconnected') {
        await get().connect();
      }

      await wait(100);
    }

    throw new Error('WebSocket connection unavailable after retry window');
  },


  resetConnection: async () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    closeSocket(get().webSocket, 1000, 'reset');
    set({
      webSocket: null,
      activeStreamId: null,
      streamContent: '',
      isStreaming: false,
      currentStreamRecord: null,
      streamChunks: {},
      webSocketGroup: null,
      lastDoneResponse: null,
      lastStreamError: null,
      cancelledStreamId: null,
      connectionStatus: 'disconnected',
      reconnectAttempts: 0,
    });
    await get().connect();
  },

  disconnect: () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    const { webSocket } = get();
    closeSocket(webSocket, 1000, 'disconnect');
    set({
      webSocket: null,
      activeStreamId: null,
      streamContent: '',
      isStreaming: false,
      currentStreamRecord: null,
      streamChunks: {},
      webSocketGroup: null,
      lastDoneResponse: null,
      lastStreamError: null,
      cancelledStreamId: null,
      connectionStatus: 'disconnected',
      reconnectAttempts: 0,
    });
  },

  stopThinking: () => {
    get().cancelChat();
  },

  sendChat: (payload: Record<string, unknown>) => {
    const { webSocket } = get();
    if (webSocket) {
      sendChatOverWs(webSocket, payload);
    } else {
      console.error('Cannot send chat: WebSocket not connected');
    }
  },

  cancelChat: () => {
    const { activeStreamId, currentStreamRecord, webSocket } = get();
    const cancelledStreamId = currentStreamRecord?.messageId || activeStreamId;
    if (webSocket) {
      cancelChatOverWs(webSocket);
      setTimeout(() => closeSocket(webSocket, 1000, 'cancel'), 0);
    }
    set({
      webSocket: null,
      webSocketGroup: null,
      cancelledStreamId,
      lastStreamError: null,
      isStreaming: false,
      connectionStatus: 'disconnected',
      reconnectAttempts: 0,
    });
  },
}));
